import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as commerce from '@/services/commerceService'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { Card, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { SkeletonListPage } from '@/components/ui/Skeleton'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'
import { STANDARD_ADDRESS_PLACEHOLDER } from '@/lib/formatStandardAddress'

export default function PickupLocationsPage() {
  const { currentStoreId: storeId, storeRecord, loading: storeContextLoading } = useCurrentStore()
  const [pickups, setPickups] = useState<commerce.PickupLocationRow[]>([])
  const [pickupName, setPickupName] = useState('')
  const [pickupAddress, setPickupAddress] = useState('')
  const [pickupFee, setPickupFee] = useState('')
  const [pickupEditId, setPickupEditId] = useState<number | null>(null)
  const [pickupSaving, setPickupSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pickupModalOpen, setPickupModalOpen] = useState(false)

  const storeCurrency = storeRecord?.currency ?? 'USD'

  const refresh = useCallback(async () => {
    if (!storeId) return
    setLoading(true)
    setError(null)
    try {
      const pu = await commerce.fetchPickupLocations(storeId)
      setPickups(pu.items || [])
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load pickup locations'))
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    if (storeId) void refresh()
  }, [storeId, refresh])

  function resetPickupForm() {
    setPickupEditId(null)
    setPickupName('')
    setPickupAddress('')
    setPickupFee('')
  }

  function closePickupModal() {
    setPickupModalOpen(false)
    resetPickupForm()
  }

  function openAddPickupModal() {
    resetPickupForm()
    setPickupModalOpen(true)
  }

  function startEditPickup(row: commerce.PickupLocationRow) {
    setPickupEditId(row.id)
    setPickupName(row.name)
    setPickupAddress(row.addressText)
    setPickupFee(row.feeAmount === '0.00' || row.feeAmount === '0' ? '' : row.feeAmount)
  }

  async function submitPickupForm() {
    if (!storeId) return
    const name = pickupName.trim()
    const addressText = pickupAddress.trim()
    if (!name || !addressText) {
      setError('Pickup name and address are required.')
      return
    }
    const feeRaw = pickupFee.trim()
    const feeNum = feeRaw === '' ? 0 : Number(feeRaw)
    if (!Number.isFinite(feeNum) || feeNum < 0) {
      setError('Fee must be a valid number (0 or empty for free).')
      return
    }
    setPickupSaving(true)
    setError(null)
    try {
      if (pickupEditId != null) {
        await commerce.updatePickupLocation(storeId, pickupEditId, {
          name,
          addressText,
          feeAmount: feeNum,
        })
      } else {
        await commerce.createPickupLocation(storeId, { name, addressText, feeAmount: feeNum })
      }
      resetPickupForm()
      setPickupModalOpen(false)
      await refresh()
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not save pickup location'))
    } finally {
      setPickupSaving(false)
    }
  }

  async function removePickup(id: number) {
    if (!storeId) return
    if (!window.confirm('Delete this pickup location?')) return
    setError(null)
    try {
      await commerce.deletePickupLocation(storeId, id)
      if (pickupEditId === id) {
        resetPickupForm()
        setPickupModalOpen(false)
      }
      await refresh()
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not delete pickup location'))
    }
  }

  if (storeContextLoading || (loading && pickups.length === 0 && !error)) {
    return <SkeletonListPage toolbarFields={0} columns={4} rows={4} />
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Pickup locations"
          description="Shoppers choose one of these when pickup is enabled at checkout. Leave fee empty or 0 for free pickup."
          action={
            <Button type="button" onClick={openAddPickupModal} disabled={!storeId}>
              Add location
            </Button>
          }
        />
        <p className="-mt-2 mb-4 text-sm text-muted-foreground">
          Enable pickup at checkout under{' '}
          <Link to="/commerce" className="font-medium text-primary underline-offset-4 hover:underline">
            Payments → Checkout & fulfillment
          </Link>
          .
        </p>
        {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Button type="button" variant="secondary" onClick={() => void refresh()} disabled={loading || !storeId}>
            Refresh
          </Button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-2">S/N</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Address</th>
                <th className="px-4 py-2 tabular-nums">Fee ({storeCurrency})</th>
                <th className="px-4 py-2 w-32"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {pickups.map((row, rowIndex) => (
                <tr key={row.id}>
                  <td className="px-4 py-2 tabular-nums">{rowIndex + 1}</td>
                  <td className="px-4 py-2 font-medium text-foreground">{row.name}</td>
                  <td className="max-w-md px-4 py-2 whitespace-pre-wrap text-muted-foreground">{row.addressText}</td>
                  <td className="px-4 py-2 tabular-nums">{row.feeAmount}</td>
                  <td className="space-x-2 px-4 py-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        startEditPickup(row)
                        setPickupModalOpen(true)
                      }}
                    >
                      Edit
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => void removePickup(row.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {pickups.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    No pickup locations yet. Add one, then enable Pickup in{' '}
                    <Link to="/commerce" className="text-primary underline-offset-4 hover:underline">
                      Payments
                    </Link>
                    .
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Modal
          open={pickupModalOpen}
          title={pickupEditId != null ? 'Edit pickup location' : 'Add pickup location'}
          onClose={closePickupModal}
          size="md"
        >
          <div className="space-y-3 text-foreground">
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Name</span>
              <Input
                value={pickupName}
                onChange={(e) => setPickupName(e.target.value)}
                placeholder="e.g. Main warehouse"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Address / instructions</span>
              <textarea
                value={pickupAddress}
                onChange={(e) => setPickupAddress(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
                placeholder={`${STANDARD_ADDRESS_PLACEHOLDER} — include hours or collection instructions if needed`}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Fee (optional, 0 or empty = free)</span>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={pickupFee}
                onChange={(e) => setPickupFee(e.target.value)}
                className="max-w-[12rem]"
              />
            </label>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" onClick={() => void submitPickupForm()} disabled={pickupSaving || !storeId}>
                {pickupSaving ? 'Saving…' : pickupEditId != null ? 'Update' : 'Add location'}
              </Button>
              <Button type="button" variant="secondary" onClick={closePickupModal}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      </Card>
    </div>
  )
}
